import axios from 'axios';
import Bugsnag from '@bugsnag/js';

import { constants } from '../config';
import { ApiError } from '../models/ApiError';
import { ApiErrorSerializer } from './serializers/ApiErrorSerializer';

const METHODS = {
  delete: 'delete',
  get: 'get',
  post: 'post',
  put: 'put',
};

const API_URLS = {
  primary: constants.apiUrl,
  secondary: constants.secondaryApiUrl,
  new_api: constants.newApiUrl,
};

class ApiHttpServiceClass {
  constructor(source) {
    this.axios = axios.create({
      baseURL: API_URLS[source],
    });
    this._addedHeaders = {};
    this._authCallback = null;
    this._queryParams = {};
    this.source = source;
  }

  async _sendRequest(method, url, config = {}) {
    const updatedConfig = { ...config };
    // If the endpoint is authenticated, call the authentication callback;
    let authToken = {};
    if (updatedConfig.authenticated && this._authCallback) {
      authToken = {
        Authorization: await this._authCallback(),
      };
      delete updatedConfig.authenticated;
    }
    updatedConfig.headers = Object.assign({}, this._addedHeaders, authToken, config.headers || {});
    try {
      if (method === METHODS.get || method === METHODS.delete) {
        return await this.axios[method](url, updatedConfig);
      } else if (method === METHODS.post || method === METHODS.put) {
        const body = updatedConfig.body || {};
        delete updatedConfig.body;
        return await this.axios[method](url, body, updatedConfig);
      }
      return null;
    } catch (error) {
      Bugsnag.addMetadata('payload', config);
      Bugsnag.notify(error);
      if (this.source === 'new_api') {
        this.processErrorNewApi(error);
      } else {
        this.processError(error);
      }
      throw error;
    }
  }

  processError(error) {
    if (error.response) {
      if (error.response.status >= 500) {
        throw new ApiError({
          message: 'Server Error',
          status: error.response.status,
          code: null,
        });
      } else if (error.response.data) {
        throw new ApiError({
          ...ApiErrorSerializer.deSerialize(error.response.data),
          status: error.response.status,
        });
      } else if (error.response.status === 401 || error.response.status === 412) {
        throw new ApiError({
          code: 401,
          message: 'La petición ha sido rechazada por problemas de seguridad',
          content: 'La petición ha sido rechazada por problemas de seguridad',
          status: error.response.status,
        });
      }
    }
  }

  processErrorNewApi(error) {
    if (error.response) {
      if (error.response && error.response.data && error.response.data.error &&
        error.response.data.error.metadata.StatusCode === 400) {
        throw new ApiError({
          status: 400,
          message: 'La petición ha sido rechazada por problemas de seguridad',
          content: 'La petición ha sido rechazada por problemas de seguridad',
        });
      } else if (error.response && error.response.data && error.response.data.error &&
        error.response.data.error.metadata.StatusCode === 401) {
        throw new ApiError({
          code: 401,
          message: 'La petición ha sido rechazada por problemas de seguridad',
          content: 'La petición ha sido rechazada por problemas de seguridad',
        });
      } else if (error.response && error.response.data && error.response.data.error &&
        error.response.data.error.metadata.StatusCode === 404) {
        throw new ApiError({
          status: 404,
          message: 'La petición ha sido rechazada por problemas de seguridad',
          content: 'La petición ha sido rechazada por problemas de seguridad',
        });
      } else if (error.response && error.response.data && error.response.data.error &&
        error.response.data.error.metadata.StatusCode === 422) {
        throw new ApiError({
          status: 422,
          message: 'Errores de validación',
          content: error.response.data.error.metadata.Errors,
        });
      } else if (error.response && error.response.data && error.response.data.error &&
        error.response.data.error.metadata.StatusCode === 500) {
        throw new ApiError({
          code: 500,
          message: 'Server Error',
        });
      } else {
        throw new ApiError({
          message: 'Error desconocido',
          content: 'Error desconocido',
        });
      }
    }
  }

  /*
    Registers a callback to call on every authenticated endpoint.
    These callbacks can be async and will be waited upon.
    NOTE: only one auth callback can be set, since only
    one header is created (Authorization).
  */
  onAuth(callback) {
    this._authCallback = callback;
  }

  setHeaders(newHeaders) {
    Object.assign(this._addedHeaders, newHeaders);
  }

  get(url, params = {}, authenticated = false, config = {}) {
    return this._sendRequest(METHODS.get, url, { ...config, params, authenticated });
  }

  post(url, body = {}, authenticated = false, config = {}) {
    return this._sendRequest(METHODS.post, url, { ...config, body, authenticated });
  }

  put(url, body = {}, authenticated = false, config = {}) {
    return this._sendRequest(METHODS.put, url, { ...config, body, authenticated });
  }

  delete(url, params = {}, authenticated = false, config = {}) {
    return this._sendRequest(METHODS.delete, url, { ...config, params, authenticated });
  }
}

const ApiHttpService = new ApiHttpServiceClass('primary');
const ApiHttpServiceSecondary = new ApiHttpServiceClass('secondary');
const ApiHttpServiceNewApi = new ApiHttpServiceClass('new_api');

export { ApiHttpService, ApiHttpServiceSecondary, ApiHttpServiceNewApi };



// WEBPACK FOOTER //
// ./src/networking/ApiHttpService.js